<template>
    <v-container
        fluid
        class="pt-0 mx-0 px-0"
    >
        <!--      Selector de anios + exportacion-->
        <v-row>
            <v-col cols="12">
                <base-card padding>
                    <div class="text-h6 font-weight-bold">
                        {{ $t('admin.editions.title') }}
                    </div>
                    <div class="text-body-2 text--disabled">
                        {{ $t('admin.editions.subtitle') }}
                    </div>

                    <v-row
                        class="mt-1"
                        align="center"
                    >
                        <v-col
                            cols="12"
                            md="8"
                        >
                            <v-select
                                v-model="selectedYearsModel"
                                :items="years"
                                :label="$t('admin.editions.yearsLabel')"
                                item-value="year"
                                item-text="year"
                                multiple
                                chips
                                hide-details
                                :disabled="!years.length"
                            >
                                <template v-slot:selection="data">
                                    <v-chip
                                        v-bind="data.attrs"
                                        :input-value="data.selected"
                                        close
                                        @click="data.select"
                                        @click:close="removeYear(data.item)"
                                    >
                                        {{ data.item.year }}
                                    </v-chip>
                                </template>

                                <template v-slot:item="{ item, attrs }">
                                    <v-list-item-action>
                                        <v-simple-checkbox
                                            :value="attrs.inputValue"
                                            :ripple="false"
                                        />
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.year }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.label }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <!-- Color explicito y no 'primary': en este tema
                                             primary es #ffffff (themeConfig.js), de modo que
                                             un chip con `dark` queda blanco sobre blanco. -->
                                        <v-chip
                                            v-if="item.isLive"
                                            x-small
                                            color="blue darken-2"
                                            dark
                                        >
                                            {{ $t('admin.editions.inProgress') }}
                                        </v-chip>
                                        <v-chip
                                            v-else-if="item.isClosed"
                                            x-small
                                        >
                                            {{ $t('admin.editions.closed') }}
                                        </v-chip>
                                    </v-list-item-action>
                                </template>
                            </v-select>
                        </v-col>

                        <v-col
                            cols="12"
                            md="4"
                            class="text-md-right"
                        >
                            <v-btn
                                color="primary"
                                dark
                                :disabled="!comparisonYearsAsc.length"
                                @click="exportComparison"
                            >
                                <v-icon>mdi-file-excel</v-icon>
                                {{ $t('admin.editions.export') }}
                            </v-btn>
                        </v-col>
                    </v-row>

                    <div
                        v-if="hasLiveYear"
                        class="text-caption text--disabled"
                    >
                        {{ $t('admin.editions.liveHint') }}
                    </div>
                </base-card>
            </v-col>
        </v-row>

        <!--      Sin ediciones: no hay nada que comparar todavia-->
        <v-row v-if="loadedYears && !years.length">
            <v-col cols="12">
                <v-alert
                    type="info"
                    text
                    prominent
                    icon="mdi-database-clock"
                >
                    {{ $t('admin.editions.noEditions') }}
                </v-alert>
            </v-col>
        </v-row>

        <v-row v-else-if="loadedYears && !selectedYears.length">
            <v-col cols="12">
                <v-alert
                    type="info"
                    text
                >
                    {{ $t('admin.editions.noYearsSelected') }}
                </v-alert>
            </v-col>
        </v-row>

        <template v-else-if="loadedYears">
            <!--      Tarjetas KPI del anio mas reciente seleccionado-->
            <v-row v-if="latestEdition">
                <v-col
                    v-for="kpi in kpis"
                    :key="kpi.key"
                    cols="12"
                    sm="6"
                    md="3"
                >
                    <base-card padding>
                        <div class="text-body-2 text--disabled">
                            {{ kpi.label }} · {{ latestYear }}
                        </div>
                        <div class="text-h5 font-weight-bold">
                            {{ kpi.value }}
                        </div>
                        <div
                            v-if="kpi.delta !== null"
                            class="d-flex align-center flex-wrap mt-1"
                        >
                            <v-icon
                                small
                                :class="kpi.deltaClass"
                            >
                                {{ kpi.icon }}
                            </v-icon>
                            <span
                                class="text-caption font-weight-bold ml-1"
                                :class="kpi.deltaClass"
                            >
                                {{ kpi.deltaText }}
                            </span>
                            <span class="text-caption text--disabled ml-1">
                                {{ $t('admin.editions.comparedWith', { year: kpi.previousYear }) }}
                            </span>
                        </div>
                    </base-card>
                </v-col>
            </v-row>

            <!--      Graficas-->
            <v-row>
                <v-col
                    cols="12"
                    md="6"
                >
                    <base-card>
                        <v-card-title>{{ $t('admin.editions.chartValues') }}</v-card-title>
                        <v-card-text>
                            <apexchart
                                v-if="chartValue"
                                type="bar"
                                height="320"
                                :options="chartValue.options"
                                :series="chartValue.series"
                            />
                        </v-card-text>
                    </base-card>
                </v-col>

                <v-col
                    cols="12"
                    md="6"
                >
                    <base-card>
                        <v-card-title>{{ $t('admin.editions.chartBehaviours') }}</v-card-title>
                        <v-card-text>
                            <apexchart
                                v-if="chartBehaviour"
                                type="bar"
                                height="320"
                                :options="chartBehaviour.options"
                                :series="chartBehaviour.series"
                            />
                        </v-card-text>
                    </base-card>
                </v-col>

                <v-col
                    cols="12"
                    md="8"
                >
                    <base-card>
                        <v-card-title>{{ $t('admin.editions.chartMonths') }}</v-card-title>
                        <v-card-text>
                            <apexchart
                                v-if="chartMonth"
                                type="line"
                                height="320"
                                :options="chartMonth.options"
                                :series="chartMonth.series"
                            />
                        </v-card-text>
                    </base-card>
                </v-col>

                <v-col
                    cols="12"
                    md="4"
                >
                    <base-card>
                        <v-card-title>{{ $t('admin.editions.chartParticipation') }}</v-card-title>
                        <v-card-text>
                            <apexchart
                                v-if="chartParticipation"
                                type="radialBar"
                                height="320"
                                :options="chartParticipation.options"
                                :series="chartParticipation.series"
                            />
                        </v-card-text>
                    </base-card>
                </v-col>
            </v-row>

            <!--      Desglose por pais / centro / sociedad-->
            <v-row>
                <v-col cols="12">
                    <base-card>
                        <v-card-title>
                            {{ $t('admin.editions.breakdownTitle') }}
                            <v-spacer />
                            <v-text-field
                                v-model="search"
                                :append-icon="!search ? 'mdi-magnify' : undefined"
                                clearable
                                :label="$t('common.search')"
                                single-line
                                hide-details
                            />
                        </v-card-title>

                        <v-card-text>
                            <v-btn-toggle
                                v-model="dimension"
                                mandatory
                                dense
                            >
                                <v-btn
                                    v-for="option in dimensionOptions"
                                    :key="option.value"
                                    small
                                    :value="option.value"
                                >
                                    {{ option.text }}
                                </v-btn>
                            </v-btn-toggle>
                        </v-card-text>

                        <v-data-table
                            :headers="breakdownHeaders"
                            :items="breakdownItems"
                            :search="search"
                            :sort-by="breakdownSortBy"
                            :sort-desc="true"
                            :multi-sort="false"
                            :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
                            :items-per-page="10"
                            :no-data-text="$t('admin.editions.noResults')"
                            :no-results-text="$t('admin.editions.noResults')"
                            class="elevation-1"
                        >
                            <template #item.label="{ value }">
                                <div :inner-html.prop="value | highlight(search)" />
                            </template>

                            <template #item.variation="{ item }">
                                <span :class="deltaClass(item.variation)">
                                    {{ formatDelta(item.variation) }}
                                    <span
                                        v-if="item.variationPercent !== null"
                                        class="text-caption"
                                    >
                                        ({{ formatDelta(item.variationPercent) }} %)
                                    </span>
                                </span>
                            </template>
                        </v-data-table>
                    </base-card>
                </v-col>
            </v-row>

            <!--      Rankings del anio elegido-->
            <v-row>
                <v-col cols="12">
                    <base-card padding>
                        <div class="d-flex align-center flex-wrap">
                            <span class="text-subtitle-1 font-weight-bold">
                                {{ $t('admin.editions.topTitle') }}
                            </span>
                            <v-spacer />
                            <v-select
                                v-model="topYear"
                                :items="comparisonYearsDesc"
                                :label="$t('admin.editions.topYear')"
                                dense
                                hide-details
                                style="max-width: 160px"
                            />
                        </div>
                    </base-card>
                </v-col>

                <v-col
                    cols="12"
                    md="6"
                >
                    <base-card>
                        <v-card-title>{{ $t('admin.editions.topRecognized') }}</v-card-title>
                        <v-data-table
                            :headers="topHeaders"
                            :items="topRecognized"
                            :sort-by="'position'"
                            :multi-sort="false"
                            hide-default-footer
                            :items-per-page="10"
                            :no-data-text="$t('admin.editions.noResults')"
                            class="elevation-1"
                        />
                    </base-card>
                </v-col>

                <v-col
                    cols="12"
                    md="6"
                >
                    <base-card>
                        <v-card-title>{{ $t('admin.editions.topSenders') }}</v-card-title>
                        <v-data-table
                            :headers="topHeaders"
                            :items="topSenders"
                            :sort-by="'position'"
                            :multi-sort="false"
                            hide-default-footer
                            :items-per-page="10"
                            :no-data-text="$t('admin.editions.noResults')"
                            class="elevation-1"
                        />
                    </base-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
import axios from '@/plugins/axios'
import { mapGetters } from 'vuex'
import typesRecognitions from '@/data/typesRecognitions'
import typesBehaviours from '@/data/typesBehaviours'

// Un color por año comparado. El selector limita a 4, así que con 4 basta.
const YEAR_COLORS = ['#006ff5', '#2D99FF', '#83CFFF', '#F5A623'];

// Dimensiones que se piden de una sola vez: cambiar de pestaña en la tabla o
// de año en los rankings no debe provocar otra llamada al servidor.
const DIMENSIONS = ['value', 'behaviour', 'month', 'country', 'centre', 'company'];

// Mismo valor que config('editions.null_bucket_key') en el backend.
const NULL_BUCKET_KEY = '__null__';

// Máximo de años comparables a la vez (el backend admite 5, aquí 4 por legibilidad).
const MAX_YEARS = 4;

// ApexCharts revienta si defaultLocale no está entre los locales declarados,
// por eso se declaran los dos idiomas reales de la aplicación en cada gráfica.
const CHART_LOCALES = [{
    name: 'es',
    options: {
        months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        days: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        shortDays: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa']
    }
}, {
    name: 'en',
    options: {
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    }
}];

export default {
    name: 'EditionsComparison',
    data: function () {
        return {
            loadedYears: false,
            years: [], // GET editions -> years
            currentYear: null,
            selectedYears: [],
            comparison: null,
            // Clave de la última comparativa pedida: evita recargas inútiles y
            // descarta respuestas que llegan tarde si el usuario cambia de años.
            requestedKey: '',
            search: '',
            dimension: 'country',
            topYear: null,
            /*
             * Cada gráfica se guarda entera (series + options) y se reemplaza el
             * objeto completo al recalcular. En Vue 2 asignar por índice
             * (this.chart.series[0] = x) NO es reactivo: el watcher de
             * vue-apexcharts no salta y la gráfica se queda con los datos
             * anteriores. En purposesCloudHistory.vue se hace así y solo cuela
             * porque el v-if monta la gráfica después de rellenarla, una única
             * vez. Aquí las series cambian cada vez que se tocan los años, así
             * que ese atajo no vale: no lo copies.
             */
            chartValue: null,
            chartBehaviour: null,
            chartMonth: null,
            chartParticipation: null
        }
    },
    computed: {
        ...mapGetters('languages', ['getLang']),

        // El idioma de la app puede ser cualquiera de los ficheros de lang; para
        // ApexCharts y para el backend solo hay es/en.
        chartLocale() {
            return this.getLang === 'en' ? 'en' : 'es';
        },
        numberLocale() {
            return this.chartLocale === 'en' ? 'en-GB' : 'es-ES';
        },
        monthLabels() {
            const locale = CHART_LOCALES.find(item => item.name === this.chartLocale);
            return locale.options.shortMonths;
        },
        selectedYearsModel: {
            get() {
                return this.selectedYears;
            },
            set(value) {
                let years = value;

                if (years.length > MAX_YEARS) {
                    // v-select añade siempre al final, así que el sobrante es el último.
                    years = years.slice(0, MAX_YEARS);
                    this.$store.dispatch('snackbar/error', this.$t('admin.editions.maxYearsWarning', { max: MAX_YEARS }));
                }

                this.selectedYears = years;
            }
        },
        hasLiveYear() {
            return this.years.some(edition => this.selectedYears.includes(edition.year) && edition.isLive);
        },

        // Los años que se pintan salen de la respuesta, no del selector: mientras
        // la petición está en vuelo las tablas siguen cuadrando con los datos.
        comparisonYearsAsc() {
            const years = this.comparison?.years ?? [];
            return [...years].sort((a, b) => a - b);
        },
        comparisonYearsDesc() {
            return [...this.comparisonYearsAsc].reverse();
        },
        latestYear() {
            const years = this.comparisonYearsAsc;
            return years.length ? years[years.length - 1] : null;
        },
        oldestYear() {
            const years = this.comparisonYearsAsc;
            return years.length ? years[0] : null;
        },
        sortedEditions() {
            const editions = this.comparison?.editions ?? [];
            return [...editions].sort((a, b) => a.year - b.year);
        },
        latestEdition() {
            const editions = this.sortedEditions;
            return editions.length ? editions[editions.length - 1] : null;
        },
        previousEdition() {
            const editions = this.sortedEditions;
            return editions.length > 1 ? editions[editions.length - 2] : null;
        },
        kpis() {
            const latest = this.latestEdition;
            if (!latest) {
                return [];
            }

            const previous = this.previousEdition;

            const definitions = [
                { key: 'recognitions', label: this.$t('admin.editions.kpiRecognitions'), field: 'recognitions', percent: false },
                { key: 'participation', label: this.$t('admin.editions.kpiParticipation'), field: 'participationRate', percent: true },
                { key: 'recognized', label: this.$t('admin.editions.kpiRecognized'), field: 'usersRecognized', percent: false },
                { key: 'likes', label: this.$t('admin.editions.kpiLikes'), field: 'likes', percent: false }
            ];

            return definitions.map(definition => {
                const value = latest.totals?.[definition.field] ?? 0;

                const card = {
                    key: definition.key,
                    label: definition.label,
                    value: definition.percent ? this.formatPercent(value) : this.formatNumber(value),
                    delta: null,
                    deltaText: '',
                    deltaClass: '',
                    icon: '',
                    previousYear: previous ? previous.year : null
                };

                // Con un solo año no hay contra qué comparar.
                if (!previous) {
                    return card;
                }

                const previousValue = previous.totals?.[definition.field] ?? 0;
                const delta = Math.round((value - previousValue) * 100) / 100;

                card.delta = delta;
                card.deltaClass = this.deltaClass(delta);
                card.icon = delta === 0 ? 'mdi-minus' : (delta > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down');

                if (definition.percent) {
                    // La participación ya es un porcentaje: su diferencia son puntos
                    // porcentuales, no un porcentaje de crecimiento.
                    card.deltaText = this.formatDelta(delta) + ' ' + this.$t('admin.editions.percentPoints');
                } else {
                    const relative = previousValue ? Math.round((delta / previousValue) * 1000) / 10 : null;
                    card.deltaText = this.formatDelta(delta) + (relative === null ? '' : ' (' + this.formatDelta(relative) + ' %)');
                }

                return card;
            });
        },
        dimensionOptions() {
            return [
                { value: 'country', text: this.$t('admin.editions.dimensionCountry') },
                { value: 'centre', text: this.$t('admin.editions.dimensionCentre') },
                { value: 'company', text: this.$t('admin.editions.dimensionCompany') }
            ];
        },
        dimensionLabel() {
            const option = this.dimensionOptions.find(item => item.value === this.dimension);
            return option ? option.text : '';
        },
        breakdownHeaders() {
            const headers = [{
                text: this.dimensionLabel,
                value: 'label',
                align: 'start'
            }];

            for (const year of this.comparisonYearsAsc) {
                headers.push({
                    text: '' + year,
                    value: 'y' + year,
                    align: 'end'
                });
            }

            if (this.comparisonYearsAsc.length > 1) {
                headers.push({
                    text: this.$t('admin.editions.variation', { from: this.oldestYear, to: this.latestYear }),
                    value: 'variation',
                    align: 'end'
                });
            }

            return headers;
        },
        breakdownSortBy() {
            return this.latestYear ? 'y' + this.latestYear : 'label';
        },
        breakdownItems() {
            const years = this.comparisonYearsAsc;

            return this.orderedRows(this.dimension).map(row => {
                const item = { label: this.bucketLabel(this.dimension, row) };

                for (const year of years) {
                    item['y' + year] = row.values?.[year] ?? 0;
                }

                if (years.length > 1) {
                    const oldest = row.values?.[this.oldestYear] ?? 0;
                    const latest = row.values?.[this.latestYear] ?? 0;

                    item.variation = latest - oldest;
                    item.variationPercent = oldest ? Math.round((item.variation / oldest) * 1000) / 10 : null;
                }

                return item;
            });
        },
        /**
         * Las dos tablas de ranking van en media pantalla cada una. Con seis
         * columnas la tabla se desbordaba y el numero de reconocimientos, que
         * es justo el dato del ranking, quedaba fuera de la vista tras una
         * barra de desplazamiento horizontal. Sociedad y pais se consultan en
         * el desglose por dimension, que si tiene ancho para ellos.
         */
        topHeaders() {
            return [
                { text: this.$t('admin.editions.columnPosition'), value: 'position', align: 'start', width: 70 },
                { text: this.$t('admin.editions.columnPerson'), value: 'fullName' },
                { text: this.$t('admin.editions.dimensionCentre'), value: 'centro' },
                { text: this.$t('admin.editions.columnCount'), value: 'count', align: 'end', width: 120 }
            ];
        },
        topRecognized() {
            return this.topList('recognized');
        },
        topSenders() {
            return this.topList('sender');
        }
    },
    watch: {
        selectedYears(value) {
            const key = [...value].sort((a, b) => a - b).join(',');

            // Al recortar la selección por encima del máximo el contenido no cambia:
            // sin esta guarda se lanzaría otra vez la misma petición.
            if (key === this.requestedKey) {
                return;
            }

            this.fetchComparison();
        },
        getLang() {
            // Las etiquetas de los buckets las traduce el backend, así que hay que
            // volver a pedirlas al cambiar de idioma.
            if (this.selectedYears.length) {
                this.fetchComparison();
            }
        }
    },
    created() {
        this.fetchEditions();
    },
    methods: {
        async fetchEditions() {
            try {
                this.$store.dispatch('loading/start');

                const response = await axios.get('editions');

                const years = [...(response.data.years ?? [])].sort((a, b) => b.year - a.year);

                this.years = years;
                this.currentYear = response.data.currentYear ?? null;

                // Por defecto se comparan los dos años más recientes disponibles.
                this.selectedYears = years.slice(0, 2).map(edition => edition.year);
            } catch (e) {
                this.$store.dispatch('snackbar/error', this.$t('common.errorLoadingData'));
            } finally {
                this.loadedYears = true;
                this.$store.dispatch('loading/end');
            }
        },

        async fetchComparison() {
            const years = [...this.selectedYears].sort((a, b) => a - b);
            const key = years.join(',');

            this.requestedKey = key;

            if (!years.length) {
                this.comparison = null;
                this.resetCharts();
                return;
            }

            try {
                this.$store.dispatch('loading/start');

                const response = await axios
                    .get('editions/comparison?years=' + key + '&dimensions=' + DIMENSIONS.join(',') + '&lang=' + this.chartLocale);

                // Si mientras tanto se ha pedido otra combinación, esta respuesta
                // ya no vale: pintarla dejaría la pantalla desincronizada.
                if (this.requestedKey !== key) {
                    return;
                }

                this.comparison = response.data;

                this.syncTopYear();
                this.buildCharts();
            } catch (e) {
                this.$store.dispatch('snackbar/error', this.$t('common.errorLoadingData'));
            } finally {
                this.$store.dispatch('loading/end');
            }
        },

        async exportComparison() {
            const years = this.comparisonYearsAsc.join(',');

            this.$store.dispatch('loading/start');

            try {
                // El Excel recorre todas las ediciones pedidas: los 20 s por defecto
                // del cliente axios se quedan cortos.
                const response = await axios
                    .get('editions/export?years=' + years, { responseType: 'arraybuffer', timeout: 120000 });

                const blob = new Blob([response.data], { type: 'application/*' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = this.$t('admin.editions.exportFileName') + ' ' + years.replace(/,/g, '-') + ' ' + this.dateHelper.currentDateTimeToFileName() + '.xlsx';
                link._target = 'blank';
                link.click();
            } catch (e) {
                this.$store.dispatch('snackbar/error', this.$t('common.errorLoadingData'));
            }

            this.$store.dispatch('loading/end');
        },

        removeYear(item) {
            this.selectedYearsModel = this.selectedYears.filter(year => year !== item.year);
        },

        syncTopYear() {
            // El año de los rankings tiene que seguir estando entre los comparados.
            if (!this.comparisonYearsAsc.includes(this.topYear)) {
                this.topYear = this.latestYear;
            }
        },

        topList(type) {
            const lists = this.comparison?.topUsers?.[this.topYear];
            return (lists?.[type] ?? []).slice(0, 10);
        },

        orderedRows(dimension) {
            const rows = this.comparison?.breakdowns?.[dimension] ?? [];

            // El bucket de nulos va al final para que no se cuele entre los valores
            // y comportamientos, que sí tienen un orden fijo.
            return [...rows].sort((a, b) => {
                if (a.key === NULL_BUCKET_KEY) {
                    return 1;
                }
                if (b.key === NULL_BUCKET_KEY) {
                    return -1;
                }
                return ('' + a.key).localeCompare('' + b.key, undefined, { numeric: true });
            });
        },

        bucketLabel(dimension, row) {
            if (row.label) {
                return row.label;
            }

            // Red de seguridad: si un snapshot antiguo llega sin etiqueta, se usa la
            // copia local de valores y comportamientos.
            const source = dimension === 'value' ? typesRecognitions : (dimension === 'behaviour' ? typesBehaviours : null);

            if (source) {
                const found = source.find(item => ('' + item.id) === ('' + row.key));
                if (found) {
                    return this.chartLocale === 'en' ? found.name_en : found.name_es;
                }
            }

            return '' + row.key;
        },

        buildCharts() {
            this.buildBucketChart('value', 'chartValue');
            this.buildBucketChart('behaviour', 'chartBehaviour');
            this.buildMonthChart();
            this.buildParticipationChart();
        },

        resetCharts() {
            this.chartValue = null;
            this.chartBehaviour = null;
            this.chartMonth = null;
            this.chartParticipation = null;
        },

        buildBucketChart(dimension, target) {
            const rows = this.orderedRows(dimension);

            if (!rows.length) {
                this[target] = null;
                return;
            }

            const categories = rows.map(row => this.bucketLabel(dimension, row));

            const series = this.comparisonYearsAsc.map(year => ({
                name: '' + year,
                data: rows.map(row => row.values?.[year] ?? 0)
            }));

            // Objeto nuevo entero: ver el comentario de data().
            this[target] = {
                series: series,
                options: this.barOptions(categories)
            };
        },

        buildMonthChart() {
            const rows = this.comparison?.breakdowns?.month ?? [];

            if (!rows.length) {
                this.chartMonth = null;
                return;
            }

            const valuesByKey = {};
            for (const row of rows) {
                valuesByKey[row.key] = row.values ?? {};
            }

            // El eje siempre tiene los 12 meses aunque el backend no devuelva
            // buckets para los meses sin actividad.
            const categories = [...this.monthLabels];

            const series = this.comparisonYearsAsc.map(year => ({
                name: '' + year,
                data: categories.map((label, index) => {
                    const key = ('' + (index + 1)).padStart(2, '0');
                    return valuesByKey[key]?.[year] ?? 0;
                })
            }));

            this.chartMonth = {
                series: series,
                options: this.lineOptions(categories)
            };
        },

        buildParticipationChart() {
            const editions = this.sortedEditions;

            if (!editions.length) {
                this.chartParticipation = null;
                return;
            }

            this.chartParticipation = {
                series: editions.map(edition => edition.totals?.participationRate ?? 0),
                options: this.radialOptions(editions.map(edition => '' + edition.year))
            };
        },

        barOptions(categories) {
            return {
                chart: {
                    type: 'bar',
                    height: 320,
                    toolbar: { show: false },
                    defaultLocale: this.chartLocale,
                    locales: CHART_LOCALES
                },
                colors: YEAR_COLORS,
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '60%',
                        borderRadius: 4
                    }
                },
                dataLabels: { enabled: false },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                legend: {
                    show: true,
                    position: 'top'
                },
                xaxis: {
                    categories: categories,
                    labels: {
                        trim: true,
                        hideOverlappingLabels: false,
                        style: { fontSize: '11px' }
                    }
                },
                tooltip: {
                    y: {
                        formatter: value => this.formatNumber(value)
                    }
                }
            };
        },

        lineOptions(categories) {
            return {
                chart: {
                    type: 'line',
                    height: 320,
                    toolbar: { show: false },
                    defaultLocale: this.chartLocale,
                    locales: CHART_LOCALES
                },
                colors: YEAR_COLORS,
                dataLabels: { enabled: false },
                stroke: {
                    curve: 'smooth',
                    width: 3
                },
                markers: { size: 4 },
                legend: {
                    show: true,
                    position: 'top'
                },
                xaxis: { categories: categories },
                tooltip: {
                    y: {
                        formatter: value => this.formatNumber(value)
                    }
                }
            };
        },

        radialOptions(labels) {
            return {
                chart: {
                    type: 'radialBar',
                    height: 320,
                    defaultLocale: this.chartLocale,
                    locales: CHART_LOCALES
                },
                colors: YEAR_COLORS,
                labels: labels,
                stroke: { lineCap: 'round' },
                plotOptions: {
                    radialBar: {
                        hollow: { size: '40%' },
                        dataLabels: {
                            name: { fontSize: '13px' },
                            value: {
                                fontSize: '16px',
                                fontWeight: 700,
                                formatter: value => this.formatPercent(value)
                            }
                        }
                    }
                },
                legend: {
                    show: true,
                    position: 'bottom'
                }
            };
        },

        formatNumber(value) {
            // numberHelper.formatInteger devuelve null cuando el valor es 0 y aquí
            // el 0 es un dato tan válido como cualquier otro.
            return new Intl.NumberFormat(this.numberLocale).format(Number(value ?? 0));
        },

        formatPercent(value) {
            return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 2 }).format(Number(value ?? 0)) + ' %';
        },

        formatDelta(value) {
            const number = Number(value ?? 0);
            return (number > 0 ? '+' : '') + this.formatNumber(number);
        },

        deltaClass(value) {
            const number = Number(value ?? 0);

            if (number === 0) {
                return 'text--disabled';
            }

            return number > 0 ? 'success--text' : 'error--text';
        }
    }
}
</script>

<style scoped>

</style>
